import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Pix as PixIcon,
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { paymentRequestsApi } from '@/services/api';

interface PaymentRequest {
  id: string;
  kind: 'boleto' | 'pix_copia_cola' | 'pix_chave';
  status: string;
  description: string | null;
  amount: string;
  due_date: string | null;
  barcode: string | null;
  pix_payload: string | null;
  pix_key: string | null;
  pix_key_type: string | null;
  beneficiary_name: string | null;
  beneficiary_document: string | null;
  external_id: string | null;
  requested_by_name: string | null;
  app_name: string | null;
  paid_at: string | null;
  paid_amount: string | null;
  end_to_end_id: string | null;
  payment_id: string | null;
  error_message: string | null;
  rejected_reason: string | null;
  created_at: string;
}

const STATUS_META: Record<string, { label: string; color: any }> = {
  pending: { label: 'Pendente', color: 'warning' },
  processing: { label: 'Processando', color: 'info' },
  paid: { label: 'Pago', color: 'success' },
  rejected: { label: 'Rejeitado', color: 'default' },
  failed: { label: 'Falhou', color: 'error' },
  cancelled: { label: 'Cancelado', color: 'default' },
};

const KIND_META: Record<string, { label: string; icon: React.ReactNode }> = {
  boleto: { label: 'Boleto', icon: <ReceiptIcon fontSize="small" /> },
  pix_copia_cola: { label: 'PIX copia-e-cola', icon: <PixIcon fontSize="small" /> },
  pix_chave: { label: 'PIX chave', icon: <PixIcon fontSize="small" /> },
};

const formatCurrency = (v: string | number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v));

const formatDateTime = (v: string | null) =>
  v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';

const SolicitacoesPagamentoPage: React.FC = () => {
  const [requests, setRequests] = useState<PaymentRequest[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PaymentRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [comprovante, setComprovante] = useState<any | null>(null);
  const [comprovanteLoading, setComprovanteLoading] = useState<string | null>(null);
  const [waTarget, setWaTarget] = useState<PaymentRequest | null>(null);
  const [waNumber, setWaNumber] = useState('');
  const [waSending, setWaSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentRequestsApi.list(
        statusFilter ? { status: statusFilter } : undefined
      );
      if (res.success) {
        setRequests(res.data || []);
        setCounts((res as any).counts || {});
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (req: PaymentRequest) => {
    if (!window.confirm(`Aprovar e PAGAR ${formatCurrency(req.amount)} via Sicoob? Esta ação efetiva o pagamento real.`)) {
      return;
    }
    setActionId(req.id);
    try {
      const res = await paymentRequestsApi.approve(req.id);
      if (res.success) {
        toast.success('Pagamento efetivado com sucesso!');
      } else {
        toast.error((res as any)?.error?.message || 'Falha ao efetivar pagamento');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Erro ao aprovar');
    } finally {
      setActionId(null);
      load();
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionId(rejectTarget.id);
    try {
      const res = await paymentRequestsApi.reject(rejectTarget.id, rejectReason || undefined);
      if (res.success) {
        toast.success('Solicitação rejeitada');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao rejeitar');
    } finally {
      setActionId(null);
      setRejectTarget(null);
      setRejectReason('');
      load();
    }
  };

  const instrumentInfo = (req: PaymentRequest) => {
    if (req.kind === 'boleto') return req.barcode || '-';
    if (req.kind === 'pix_chave') return `${req.pix_key} ${req.pix_key_type ? `(${req.pix_key_type})` : ''}`;
    return (req.pix_payload || '').slice(0, 40) + '...';
  };

  const openComprovante = async (req: PaymentRequest) => {
    setComprovanteLoading(req.id);
    try {
      const res = await paymentRequestsApi.comprovante(req.id);
      if (res.success) setComprovante(res.data);
      else toast.error('Comprovante indisponível');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Erro ao obter comprovante');
    } finally {
      setComprovanteLoading(null);
    }
  };

  const copyComprovante = () => {
    if (comprovante?.texto) {
      navigator.clipboard.writeText(comprovante.texto);
      toast.success('Comprovante copiado!');
    }
  };

  const sendWhatsapp = async () => {
    if (!waTarget) return;
    setWaSending(true);
    try {
      const res = await paymentRequestsApi.sendComprovanteWhatsapp(waTarget.id, waNumber.trim() || undefined);
      if (res.success) {
        toast.success('Comprovante enviado no WhatsApp!');
        setWaTarget(null);
        setWaNumber('');
      } else {
        toast.error((res as any)?.error?.message || 'Falha ao enviar');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Erro ao enviar no WhatsApp');
    } finally {
      setWaSending(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Solicitações de Pagamento</Typography>
          <Typography variant="body2" color="text.secondary">
            Títulos enviados por apps integrados aguardando sua aprovação para efetivação via Sicoob.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined">Atualizar</Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          exclusive
          onChange={(_, v) => setStatusFilter(v ?? '')}
        >
          <ToggleButton value="pending">Pendentes {counts.pending ? `(${counts.pending})` : ''}</ToggleButton>
          <ToggleButton value="paid">Pagas {counts.paid ? `(${counts.paid})` : ''}</ToggleButton>
          <ToggleButton value="failed">Falhas {counts.failed ? `(${counts.failed})` : ''}</ToggleButton>
          <ToggleButton value="rejected">Rejeitadas</ToggleButton>
          <ToggleButton value="">Todas</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : requests.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">Nenhuma solicitação {statusFilter ? STATUS_META[statusFilter]?.label.toLowerCase() : ''}.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descrição / Favorecido</TableCell>
                  <TableCell>Instrumento</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Criada</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req) => {
                  const st = STATUS_META[req.status] || { label: req.status, color: 'default' };
                  const kn = KIND_META[req.kind] || { label: req.kind, icon: null };
                  return (
                    <TableRow key={req.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {kn.icon}<span>{kn.label}</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{req.description || '-'}</Typography>
                        {req.beneficiary_name && (
                          <Typography variant="caption" color="text.secondary">{req.beneficiary_name}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={instrumentInfo(req)}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {instrumentInfo(req).slice(0, 24)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(req.amount)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{req.app_name || '-'}</Typography>
                        {req.requested_by_name && (
                          <Typography variant="caption" color="text.secondary" display="block">{req.requested_by_name}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={req.error_message || req.rejected_reason || ''}>
                          <Chip size="small" label={st.label} color={st.color} variant={req.status === 'pending' ? 'filled' : 'outlined'} />
                        </Tooltip>
                      </TableCell>
                      <TableCell><Typography variant="caption">{formatDateTime(req.created_at)}</Typography></TableCell>
                      <TableCell align="right">
                        {req.status === 'pending' ? (
                          actionId === req.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Aprovar e pagar">
                                <IconButton size="small" color="success" onClick={() => handleApprove(req)}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Rejeitar">
                                <IconButton size="small" color="error" onClick={() => setRejectTarget(req)}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )
                        ) : req.status === 'paid' ? (
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                            <Typography variant="caption" color="text.secondary">{formatDateTime(req.paid_at)}</Typography>
                            <Tooltip title="Comprovante">
                              <IconButton size="small" color="success" onClick={() => openComprovante(req)} disabled={comprovanteLoading === req.id}>
                                {comprovanteLoading === req.id ? <CircularProgress size={16} /> : <ReceiptIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar comprovante no WhatsApp">
                              <IconButton size="small" sx={{ color: '#25D366' }} onClick={() => { setWaNumber(''); setWaTarget(req); }}>
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <span>-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {requests.some((r) => r.status === 'failed') && statusFilter === 'failed' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Solicitações que falharam continuam registradas para auditoria. Corrija no app de origem e reenvie.
        </Alert>
      )}

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Rejeitar solicitação</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {rejectTarget && `${KIND_META[rejectTarget.kind]?.label} de ${formatCurrency(rejectTarget.amount)}`}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Motivo (opcional)"
            multiline
            rows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleReject} disabled={actionId === rejectTarget?.id}>
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!comprovante} onClose={() => setComprovante(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Comprovante de Pagamento</DialogTitle>
        <DialogContent>
          {comprovante && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Valor</Typography>
                <Typography variant="body2" fontWeight={700}>{comprovante.valorFormatado}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Data</Typography>
                <Typography variant="body2">{comprovante.dataFormatada}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Forma</Typography>
                <Typography variant="body2">{comprovante.tipoLabel}</Typography>
              </Box>
              {comprovante.favorecido?.nome && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Favorecido</Typography>
                  <Typography variant="body2">{comprovante.favorecido.nome}</Typography>
                </Box>
              )}
              {comprovante.endToEndId && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">ID transação (E2E)</Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {comprovante.endToEndId}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {comprovante.texto}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComprovante(null)}>Fechar</Button>
          <Button startIcon={<CopyIcon />} onClick={copyComprovante}>Copiar</Button>
          {comprovante?.pdf_url && (
            <Button startIcon={<PrintIcon />} onClick={() => window.open(comprovante.pdf_url, '_blank')}>PDF</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!waTarget} onClose={() => !waSending && setWaTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Enviar comprovante no WhatsApp</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {waTarget && `${KIND_META[waTarget.kind]?.label} de ${formatCurrency(waTarget.amount)}`}
          </DialogContentText>
          <TextField
            autoFocus fullWidth size="small" label="Número, JID ou grupo (opcional)"
            value={waNumber} onChange={(e) => setWaNumber(e.target.value)}
            placeholder="5537999999999 ou 120363...@g.us"
            helperText="Deixe em branco para enviar ao destino configurado (grupo de trabalho)."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaTarget(null)} disabled={waSending}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={waSending ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon />}
            onClick={sendWhatsapp}
            disabled={waSending}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SolicitacoesPagamentoPage;
